import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../utils/supabase';

const ProfileHeader = ({ user, onAvatarUpdate }) => {
  const { user: authUser, updateProfile } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${authUser.id}-${Date.now()}.${fileExt}`;
      // Path must be: {user_id}/avatars/{filename} to match RLS policy
      const filePath = `${authUser.id}/avatars/${fileName}`;

      // Upload file to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('user-uploads')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('user-uploads')
        .getPublicUrl(filePath);

      const avatarUrl = urlData.publicUrl;

      // Update user profile with new avatar URL
      const result = await updateProfile({ avatar_url: avatarUrl });

      if (result.success) {
        onAvatarUpdate(avatarUrl);
      } else {
        throw new Error(result.error || 'Failed to update profile');
      }

    } catch (error) {
      console.error('Avatar upload error:', error);
      setUploadError(error.message || 'Failed to upload avatar. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg p-6 border border-border">
      {uploadError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <div className="text-red-500 mr-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-red-700 text-sm">{uploadError}</p>
            <button 
              onClick={() => setUploadError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
        <div className="relative">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-muted">
            <Image
              src={user.avatar}
              alt={user.name}
              className="w-full h-full object-cover"
            />
          </div>
          <label className="absolute bottom-0 right-0 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full p-2 cursor-pointer transition-colors">
            <Icon name="Camera" size={16} />
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
          </label>
          {isUploading && (
            <div className="absolute inset-0 bg-background/80 rounded-full flex items-center justify-center">
              <Icon name="Loader2" size={20} className="animate-spin text-primary" />
            </div>
          )}
        </div>

        <div className="flex-1 text-center sm:text-left">
          <h1 className="text-2xl font-bold text-foreground">{user.name}</h1>
          <p className="text-muted-foreground">{user.email}</p>
          <div className="flex flex-wrap justify-center sm:justify-start gap-4 mt-3 text-sm">
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="text-muted-foreground">Joined {user.joinDate}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <span className="text-muted-foreground">{user.totalPlaytime} hours played</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Trophy" size={16} className="text-muted-foreground" />
              <span className="text-muted-foreground">{user.achievements} achievements</span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            user.subscriptionStatus === 'active' ?'bg-accent/10 text-accent' :'bg-warning/10 text-warning'
          }`}>
            {user.subscriptionStatus === 'active' ? 'Premium Member' : 'Subscription Expired'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
